import React, { createContext, useContext, useMemo } from "react";
import { useOrganization } from "./OrganizationContext";
import { useAuth } from "./AuthContext";

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return default permissions when PermissionsProvider is not available
    return {
      canManageMembers: false,
      canManageTournaments: false,
      canManageOrganization: false,
      canAssignRoles: false,
      canEnterScores: false,
      canRemoveMembers: false,
      canManageOrganizationBowlers: false,
      canAccessContactInfo: false,
      canModifyRole: () => false,
      canModifyMember: () => false,
      canRemoveMember: () => false,
      isOwner: false,
      isAdmin: false,
    };
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();

  const permissions = useMemo(() => {
    if (!currentOrganization?.memberRoles) {
      return {
        canManageMembers: false,
        canManageTournaments: false,
        canManageOrganization: false,
        canAssignRoles: false,
        canEnterScores: false,
        canRemoveMembers: false,
        canManageOrganizationBowlers: false,
        canAccessContactInfo: false,
        canModifyRole: () => false,
        canModifyMember: () => false,
        canRemoveMember: () => false,
        isOwner: false,
        isAdmin: false,
      };
    }

    // Get all roles for the current user
    const userRoles = currentOrganization.memberRoles.map(
      (memberRole) => memberRole.role
    );

    // Get the highest rank among user's roles
    const highestRank = Math.max(...userRoles.map((role) => role.rank || 0));

    // Check if user is owner or admin
    const isOwner = userRoles.some(
      (role) => role.name.toLowerCase() === "owner"
    );
    const isAdmin = userRoles.some(
      (role) =>
        role.name.toLowerCase() === "admin" ||
        role.name.toLowerCase() === "owner"
    );

    // Helper function to check if a role has a specific permission
    const hasPermission = (role, permission) => {
      if (!role.permissions) return false;

      // Handle string permissions (comma-separated)
      if (typeof role.permissions === "string") {
        return role.permissions.split(",").includes(permission);
      }

      // Handle array permissions
      if (Array.isArray(role.permissions)) {
        return role.permissions.includes(permission);
      }

      // Handle object permissions
      if (typeof role.permissions === "object") {
        return role.permissions[permission] === true;
      }

      return false;
    };

    // Basic permissions based on role capabilities
    const basicPermissions = {
      canManageMembers: userRoles.some((role) =>
        hasPermission(role, "manage_members")
      ),
      canManageTournaments: userRoles.some((role) =>
        hasPermission(role, "manage_tournaments")
      ),
      canManageOrganization: userRoles.some((role) =>
        hasPermission(role, "manage_organization")
      ),
      canManageOrganizationBowlers: userRoles.some((role) =>
        hasPermission(role, "manage_organization_bowlers")
      ),
      canAccessContactInfo: userRoles.some(
        (role) =>
          hasPermission(role, "access_bowler_contact_info") ||
          hasPermission(role, "manage_bowlers") ||
          hasPermission(role, "manage_organization_bowlers")
      ),
      canAssignRoles: userRoles.some((role) =>
        hasPermission(role, "assign_roles")
      ),
      canEnterScores: userRoles.some((role) =>
        hasPermission(role, "enter_scores")
      ),
      canRemoveMembers: userRoles.some((role) =>
        hasPermission(role, "remove_members")
      ),
    };

    // Helper functions for hierarchical checks
    const canModifyRole = (targetRoleRank) => {
      // Only owners can assign owner roles (rank 100)
      if (targetRoleRank === 100) return isOwner;
      // Users can only modify roles with lower rank than their highest
      return highestRank > targetRoleRank;
    };

    const canModifyMember = (memberRoles) => {
      // Users cannot modify themselves
      if (memberRoles.some((role) => role.userID === user?.id)) {
        return false;
      }

      const memberHighestRank = Math.max(
        ...memberRoles.map((role) => role.rank || 0)
      );

      // Only owners can modify other owners
      if (memberHighestRank === 100) return isOwner;

      // Users can only modify members with lower rank than their highest
      return highestRank > memberHighestRank;
    };

    const canRemoveMember = (member) => {
      // Users cannot remove themselves
      if (member.userID === user?.id) {
        return false;
      }

      const memberHighestRank = Math.max(
        ...member.memberRoles.map((role) => role.role?.rank || 0)
      );

      // Only owners can remove other owners
      if (memberHighestRank === 100) return isOwner;

      // Users can only remove members with lower rank than their highest
      return (
        highestRank > memberHighestRank && basicPermissions.canManageMembers
      );
    };

    return {
      ...basicPermissions,
      canModifyRole,
      canModifyMember,
      canRemoveMember,
      isOwner,
      isAdmin,
      userRoles,
      highestRank,
    };
  }, [currentOrganization, user]);

  return (
    <PermissionsContext.Provider value={permissions}>
      {children}
    </PermissionsContext.Provider>
  );
};
